/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, FC, FormEvent } from 'react';
import { useDispatch } from 'react-redux';
import { Box, FormControlLabel, Checkbox, Typography, Link } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { downloadService } from 'services';

import { addNotificationAction } from 'store/notifications/notificationsActions';
import { useTranslation } from 'react-i18next';
import { ShopProductDocumentTypes } from '../shared/constants/ShopProductDocumentTypes';

const useStyle = makeStyles(() => ({
  product: {},
  tnc_link: {
    textDecoration: 'underline',
  },
}));

type ShopProductItem = {
  id: string;
  name: string;
  documents: Array<ShopProductDocument>;
};

type ShopProductDocument = {
  id: string;
  name: string;
  type: ShopProductDocumentTypes;
};

type ShopTermsAndConditionsProps = {
  products: Array<ShopProductItem>;
  onAccepted: (termsAcceptance: Map<string, ShopAcceptedDocument>) => void;
  onRejected: () => void;
};

type ShopAcceptedDocument = {
  checked: boolean;
  document: ShopProductDocument | null;
};

const ShopTermsAndConditions: FC<ShopTermsAndConditionsProps> = ({
  products,
  onAccepted,
  onRejected,
}) => {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();

  const [termsAcceptance, setTermsAcceptance] = useState(new Map<string, ShopAcceptedDocument>([]));

  const findTermsDocument = (product: ShopProductItem) => {
    const termDocuments = product.documents.filter((document: ShopProductDocument) => {
      return document.type === ShopProductDocumentTypes.TERMS;
    });
    if (termDocuments?.length) {
      return termDocuments[0];
    }
    return null;
  };

  useEffect(() => {
    products.forEach((product) => {
      setTermsAcceptance(termsAcceptance.set(product.id, { checked: false, document: null }));
    });
  }, [products, termsAcceptance]);

  const handleClick = (event: React.ChangeEvent<HTMLInputElement>, product: ShopProductItem) => {
    if (termsAcceptance) {
      const document = findTermsDocument(product);
      setTermsAcceptance(
        termsAcceptance.set(product.id, { checked: event.target.checked, document }),
      );
      const allAccepted = Array.from(termsAcceptance.values()).every((value) => value.checked);
      if (allAccepted) {
        onAccepted(termsAcceptance);
      } else {
        onRejected();
      }
      return;
    }
    onRejected();
  };

  const throwFetchError = () =>
    dispatch(
      addNotificationAction({
        variant: 'error',
        message: t('SHOP.CHECKOUT.TERMS_DOWNLOAD_FAILED'),
      }),
    );

  const handleDownload = (event: FormEvent, document: ShopProductDocument) => {
    event.preventDefault();
    downloadService({
      path: `/shop/file-download/${document.id}/`,
      name: document.name,
      onError: throwFetchError,
    });
  };

  const buildTermsLabel = (product: ShopProductItem) => {
    const doc = findTermsDocument(product);

    if (doc) {
      return (
        <Typography variant="body1">
          {t('SHOP.CHECKOUT.READ_AND_ACCEPT')}
          <Link
            href="#"
            onClick={(e: FormEvent) => handleDownload(e, doc)}
            className={classes.tnc_link}
          >
            {t('SHOP.CHECKOUT.TERMS_AND_CONDITIONS')}
          </Link>
          {t('SHOP.CHECKOUT.OF_PRODUCT')}
          {product.name}
          {t('SHOP.CHECKOUT.AND_CONFIRM')}
        </Typography>
      );
    }
    return (
      <Typography variant="body1">
        {t('SHOP.CHECKOUT.READ_AND_ACCEPT')}
        {t('SHOP.CHECKOUT.TERMS_AND_CONDITIONS')}
        {t('SHOP.CHECKOUT.OF_PRODUCT')}
        {product.name}
        {t('SHOP.CHECKOUT.AND_CONFIRM')}
      </Typography>
    );
  };

  return (
    <>
      <Box>
        {products.map((product) => (
          <Box p={3} className={classes.product} key={product.id}>
            <FormControlLabel
              control={
                <Checkbox
                  name="Accept"
                  value={termsAcceptance.get(product.id)}
                  onChange={(e) => handleClick(e, product)}
                />
              }
              label={buildTermsLabel(product)}
            />
          </Box>
        ))}
      </Box>
    </>
  );
};

export { ShopTermsAndConditions };
